"use client";

import * as React from "react";
import { CheckIcon, ExternalLinkIcon, LockIcon, LogInIcon, MailIcon } from "lucide-react";
import { Button } from "../../primitives/button";
import { InputCheckbox } from "../../primitives/input-checkbox";
import { Input } from "../../primitives/input";
import {
  InputNumberCode,
  InputNumberCodeGroup,
  InputNumberCodeSlot,
} from "../../primitives/input-number-code";
import { Label } from "../../primitives/label";

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

/**
 * Internal step states for the authentication flow.
 *
 * Maps to the "Authenticate" swim lane in the UX flow diagram:
 *
 * `email` → `password` (existing user) or `auth-code` (new / no-password)
 * → `create-password-decision` → `create-password` → authenticated
 */
type AuthenticateStep =
  | "email"
  | "password"
  | "auth-code"
  | "create-password-decision"
  | "create-password";

type UserKind = "existing-with-password" | "existing-no-password" | "new";

/* ================================================================== */
/*  Utility: AccentButton                                              */
/* ================================================================== */

function AccentButton({
  accentColor,
  accentHoverColor,
  className,
  children,
  ...rest
}: {
  accentColor?: string;
  accentHoverColor?: string;
  className?: string;
  children?: React.ReactNode;
  size?:
    | "default"
    | "xs"
    | "sm"
    | "lg"
    | "icon"
    | "icon-xs"
    | "icon-sm"
    | "icon-lg";
  onClick?: () => void;
  disabled?: boolean;
}) {
  if (!accentColor) {
    return (
      <Button className={`w-full ${className ?? ""}`} {...rest}>
        {children}
      </Button>
    );
  }

  return (
    <Button
      className={`w-full text-white ${className ?? ""}`}
      style={{ backgroundColor: accentColor }}
      onMouseEnter={(e) => {
        if (accentHoverColor) {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor =
            accentHoverColor;
        }
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.backgroundColor =
          accentColor;
      }}
      {...rest}
    >
      {children}
    </Button>
  );
}

/* ================================================================== */
/*  Authenticate                                                       */
/* ================================================================== */

/**
 * Multi-step authentication block covering the full authentication flow.
 *
 * Renders the interactive form content only — consumers wrap it in whatever
 * layout they need (SplitPage, centered card, modal, etc.).
 *
 * **Flow:** email → password / auth-code → create-password-decision →
 * create-password → authenticated.
 */
function FormAuthenticate({
  accentColor,
  accentHoverColor,
  isLoading: externalIsLoading,
  error,
  onCheckEmail,
  onLogin,
  onSendAuthCode,
  onVerifyAuthCode,
  onCreatePassword,
  onAuthenticated,
  onStepChange,
  initialStep,
  guideHref,
  guideTroubleshootingHref,
  initialEmail,
}: {
  /** Custom accent colour for CTA buttons. */
  accentColor?: string;
  /** Hover variant of the accent colour. */
  accentHoverColor?: string;
  /** When true all submit buttons show a loading state. */
  isLoading?: boolean;
  /** External error message (e.g. from an API call). */
  error?: string;
  /**
   * Determine user type from email. Return value controls routing:
   * - `"existing-with-password"` → password step
   * - `"existing-no-password"` → auth code step
   * - `"new"` → auth code step
   *
   * Supports both sync and async return values.
   * When omitted all users are treated as `"new"`.
   */
  onCheckEmail?: (email: string) => UserKind | Promise<UserKind>;
  /**
   * Validate password login. Return `false` to show an error.
   * Supports both sync and async return values.
   * When omitted the password is accepted.
   */
  onLogin?: (email: string, password: string) => boolean | Promise<boolean>;
  /** Called when an auth code should be sent to the user's email. */
  onSendAuthCode?: (email: string) => void | Promise<void>;
  /**
   * Validate an auth code. Return `false` to show an error.
   * Supports both sync and async return values.
   * When omitted the code is accepted.
   */
  onVerifyAuthCode?: (email: string, code: string) => boolean | Promise<boolean>;
  /**
   * Set a new password for the authenticated user.
   * Return `false` or a string error to show an error.
   * Supports both sync and async return values.
   * When omitted the password is accepted.
   */
  onCreatePassword?: (password: string) => boolean | string | { error: string; guideHref?: string } | Promise<boolean | string | { error: string; guideHref?: string }>;
  /** Called when the authentication flow completes successfully. */
  onAuthenticated: () => void;
  /** Called whenever the internal step changes, for external step tracking. */
  onStepChange?: (step: AuthenticateStep) => void;
  /** Override the starting step (defaults to `"email"`). Useful for stories / demos. */
  initialStep?: AuthenticateStep;
  /** Link to the user guide for help (shown on email step). */
  guideHref?: string;
  /** Link to troubleshooting guide (shown on OTP code step). */
  guideTroubleshootingHref?: string;
  /** Pre-fill the email field (useful for demos / previews). */
  initialEmail?: string;
}) {
  const STORAGE_KEY = "decent-auth-remembered-email";
  const [step, setStep] = React.useState<AuthenticateStep>(initialStep ?? "email");
  const [email, setEmail] = React.useState(initialEmail ?? "");
  const [rememberEmail, setRememberEmail] = React.useState(false);
  const [password, setPassword] = React.useState("");
  const [otp, setOtp] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [userKind, setUserKind] = React.useState<UserKind>("new");
  const [validationError, setValidationError] = React.useState<
    string | undefined
  >();
  const [validationGuideHref, setValidationGuideHref] = React.useState<
    string | undefined
  >();
  const [internalLoading, setInternalLoading] = React.useState(false);
  const otpSubmitted = React.useRef(false);
  const forgotPassword = React.useRef(false);

  // Load remembered email from localStorage on mount (skip if initialEmail provided)
  React.useEffect(() => {
    if (initialEmail) return;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setEmail(saved);
        setRememberEmail(true);
      }
    } catch {
      // localStorage unavailable (SSR, privacy mode) — ignore
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = externalIsLoading || internalLoading;
  const displayError = error ?? validationError;

  function go(s: AuthenticateStep) {
    setValidationError(undefined);
    setValidationGuideHref(undefined);
    setStep(s);
    onStepChange?.(s);
  }

  /* ── step handlers ── */

  async function handleEmailSubmit() {
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setValidationError("Please enter a valid email address.");
      return;
    }
    setValidationError(undefined);

    // Save or clear remembered email
    try {
      if (rememberEmail) {
        localStorage.setItem(STORAGE_KEY, trimmed);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // localStorage unavailable — ignore
    }

    setInternalLoading(true);
    try {
      const kind = (await onCheckEmail?.(trimmed)) ?? "new";
      setUserKind(kind);
      if (kind === "existing-with-password") {
        go("password");
      } else {
        await onSendAuthCode?.(trimmed);
        go("auth-code");
      }
    } catch {
      setValidationError("Something went wrong. Please try again.");
    } finally {
      setInternalLoading(false);
    }
  }

  async function handlePasswordSubmit() {
    if (!password) {
      setValidationError("Please enter your password.");
      return;
    }
    setInternalLoading(true);
    try {
      const valid = (await onLogin?.(email, password)) ?? true;
      if (!valid) {
        setValidationError("Invalid password. Please try again.");
        return;
      }
      onAuthenticated();
    } catch {
      setValidationError("Login failed. Please try again.");
    } finally {
      setInternalLoading(false);
    }
  }

  async function handleVerifyOtp(value: string) {
    setInternalLoading(true);
    try {
      const valid = (await onVerifyAuthCode?.(email, value)) ?? true;
      if (!valid) {
        setValidationError("Invalid or expired code. Please try again.");
        setOtp("");
        otpSubmitted.current = false;
        return;
      }
      if (forgotPassword.current) {
        // User clicked "Forgot password?" — let them set a new one
        forgotPassword.current = false;
        go("create-password");
      } else if (userKind === "existing-with-password") {
        // Existing user used OTP as alternative login — already has password
        onAuthenticated();
      } else {
        go("create-password-decision");
      }
    } catch {
      setValidationError("Verification failed. Please try again.");
      setOtp("");
      otpSubmitted.current = false;
    } finally {
      setInternalLoading(false);
    }
  }

  function handleOtpChange(value: string) {
    setOtp(value);
    if (value.length === 6 && !otpSubmitted.current) {
      otpSubmitted.current = true;
      handleVerifyOtp(value);
    }
  }

  async function handleSetPassword() {
    if (!newPassword) {
      setValidationError("Please enter a password.");
      return;
    }
    if (newPassword.length < 10) {
      setValidationError("Password must be at least 10 characters.");
      return;
    }
    if (!/[a-zA-Z]/.test(newPassword) || !/\d/.test(newPassword)) {
      setValidationError("Password must contain both letters and numbers.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setValidationError("Passwords do not match.");
      return;
    }
    setInternalLoading(true);
    try {
      const result = (await onCreatePassword?.(newPassword)) ?? true;
      if (result === false) {
        setValidationError("Failed to set password. Please try again.");
        return;
      }
      if (typeof result === "string") {
        setValidationError(result);
        return;
      }
      if (typeof result === "object" && "error" in result) {
        setValidationError(result.error);
        setValidationGuideHref(result.guideHref);
        return;
      }
      onAuthenticated();
    } catch {
      setValidationError("Failed to set password. Please try again.");
    } finally {
      setInternalLoading(false);
    }
  }

  /* ── step renderers ── */

  function renderEmail() {
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    return (
      <>
        <div className="flex flex-col gap-4 w-full max-w-sm">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="auth-email" className="text-base font-semibold">Enter your email to continue</Label>
            <Input
              id="auth-email"
              type="email"
              placeholder="you@nonprofit.org"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
            />
          </div>
          <div className="flex items-center gap-2">
            <InputCheckbox
              id="auth-remember-email"
              checked={rememberEmail}
              onCheckedChange={(v) => setRememberEmail(v === true)}
            />
            <Label
              htmlFor="auth-remember-email"
              className="text-sm font-normal"
            >
              Remember my email on this device
            </Label>
          </div>
          {displayError && (
            <p className="text-destructive text-sm">{displayError}</p>
          )}
          <div className="pt-2">
            <AccentButton
              accentColor={accentColor}
              accentHoverColor={accentHoverColor}
              disabled={isLoading || !isValidEmail}
              onClick={handleEmailSubmit}
            >
              {isLoading ? "Loading\u2026" : "Continue"}
            </AccentButton>
          </div>
          {guideHref && (
            <a
              href={guideHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
            >
              Need help? Open the Guide
              <ExternalLinkIcon className="size-3" />
            </a>
          )}
        </div>
      </>
    );
  }

  function renderPassword() {
    return (
      <>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-semibold">Login</h2>
        </div>
        <div className="flex flex-col gap-4 w-full max-w-sm">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="auth-password">Enter your password to continue</Label>
            <Input
              id="auth-password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handlePasswordSubmit()}
            />
          </div>
          {displayError && (
            <p className="text-destructive text-sm">{displayError}</p>
          )}
          <AccentButton
            accentColor={accentColor}
            accentHoverColor={accentHoverColor}
            disabled={isLoading}
            onClick={handlePasswordSubmit}
          >
            {isLoading ? "Loading\u2026" : <><LogInIcon className="size-4 mr-1.5" />Login</>}
          </AccentButton>
          <Button
            variant="outline"
            className="w-full"
            disabled={isLoading}
            onClick={() => {
              setValidationError(undefined);
              onSendAuthCode?.(email);
              go("auth-code");
            }}
          >
            <MailIcon className="size-4 mr-1.5" />
            Send code to my email instead
          </Button>
          <Button
            variant="link"
            className="h-auto p-0 text-sm text-muted-foreground"
            onClick={() => {
              setValidationError(undefined);
              forgotPassword.current = true;
              onSendAuthCode?.(email);
              go("auth-code");
            }}
          >
            Forgot password?
          </Button>
        </div>
      </>
    );
  }

  function renderAuthCode() {
    return (
      <>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-semibold">Check Your Email</h2>
          <p className="text-muted-foreground text-sm mt-1">
            We emailed you a code. Enter it below:
          </p>
        </div>
        <div className="flex flex-col gap-4 w-full max-w-sm items-center">
          <InputNumberCode maxLength={6} value={otp} onChange={handleOtpChange}>
            <InputNumberCodeGroup>
              <InputNumberCodeSlot index={0} />
              <InputNumberCodeSlot index={1} />
              <InputNumberCodeSlot index={2} />
              <InputNumberCodeSlot index={3} />
              <InputNumberCodeSlot index={4} />
              <InputNumberCodeSlot index={5} />
            </InputNumberCodeGroup>
          </InputNumberCode>
          {displayError && (
            <p className="text-destructive text-sm">{displayError}</p>
          )}
          <AccentButton
            accentColor={accentColor}
            accentHoverColor={accentHoverColor}
            onClick={() => handleVerifyOtp(otp)}
            disabled={otp.length < 6 || isLoading}
          >
            {isLoading ? "Loading\u2026" : <><CheckIcon className="size-4 mr-1.5" />Verify Code</>}
          </AccentButton>
          <Button
            variant="link"
            className="h-auto p-0 text-sm text-muted-foreground"
            onClick={() => {
              setOtp("");
              otpSubmitted.current = false;
              setValidationError(undefined);
              onSendAuthCode?.(email);
            }}
          >
            Resend code
          </Button>
          <Button
            variant="link"
            className="h-auto p-0 text-sm text-muted-foreground"
            onClick={() => {
              setOtp("");
              otpSubmitted.current = false;
              go("email");
            }}
          >
            Use a different email address
          </Button>
          {guideTroubleshootingHref && (
            <a
              href={guideTroubleshootingHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mt-2 w-full"
            >
              Not getting the code? Get help
              <ExternalLinkIcon className="size-3" />
            </a>
          )}
        </div>
      </>
    );
  }

  function renderCreatePasswordDecision() {
    return (
      <>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-semibold">Set a Password?</h2>
          <p className="text-muted-foreground text-sm mt-1">
            You can set a password for faster login, or continue with email
            codes only.
          </p>
        </div>
        <div className="flex flex-col gap-3 w-full max-w-sm">
          <AccentButton
            accentColor={accentColor}
            accentHoverColor={accentHoverColor}
            onClick={() => go("create-password")}
          >
            <LockIcon className="size-4 mr-1.5" />
            Set Password
          </AccentButton>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => onAuthenticated()}
          >
            Skip for Now
          </Button>
        </div>
      </>
    );
  }

  function renderCreatePassword() {
    const hasMinLength = newPassword.length >= 10;
    const hasLetter = /[a-zA-Z]/.test(newPassword);
    const hasDigit = /\d/.test(newPassword);
    const passwordsMatch = newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword;

    return (
      <>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-semibold">Create Password</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Choose a strong password for your account
          </p>
        </div>
        <div className="flex flex-col gap-4 w-full max-w-sm">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="auth-new-password">New Password</Label>
            <Input
              id="auth-new-password"
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="auth-confirm-password">Confirm Password</Label>
            <Input
              id="auth-confirm-password"
              type="password"
              placeholder="Re-enter password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSetPassword()}
            />
          </div>

          {/* Password requirements checklist */}
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2.5">
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Password requirements:</p>
            <ul className="space-y-1">
              <li className="flex items-center gap-1.5 text-xs">
                <span className={hasMinLength ? "text-green-600" : "text-muted-foreground/60"}>{hasMinLength ? "✓" : "○"}</span>
                <span className={hasMinLength ? "text-foreground" : "text-muted-foreground"}>At least 10 characters</span>
              </li>
              <li className="flex items-center gap-1.5 text-xs">
                <span className={hasLetter ? "text-green-600" : "text-muted-foreground/60"}>{hasLetter ? "✓" : "○"}</span>
                <span className={hasLetter ? "text-foreground" : "text-muted-foreground"}>Contains at least one letter</span>
              </li>
              <li className="flex items-center gap-1.5 text-xs">
                <span className={hasDigit ? "text-green-600" : "text-muted-foreground/60"}>{hasDigit ? "✓" : "○"}</span>
                <span className={hasDigit ? "text-foreground" : "text-muted-foreground"}>Contains at least one number</span>
              </li>
              <li className="flex items-center gap-1.5 text-xs">
                <span className={passwordsMatch ? "text-green-600" : "text-muted-foreground/60"}>{passwordsMatch ? "✓" : "○"}</span>
                <span className={passwordsMatch ? "text-foreground" : "text-muted-foreground"}>Passwords match</span>
              </li>
            </ul>
          </div>

          {displayError && (
            <p className="text-destructive text-sm">{displayError}</p>
          )}
          {validationGuideHref && (
            <a
              href={validationGuideHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
            >
              For more info on password security, review the guide here
              <ExternalLinkIcon className="size-3" />
            </a>
          )}
          <AccentButton
            accentColor={accentColor}
            accentHoverColor={accentHoverColor}
            disabled={isLoading}
            onClick={handleSetPassword}
          >
            {isLoading ? "Loading\u2026" : <><LockIcon className="size-4 mr-1.5" />Set Password</>}
          </AccentButton>
          <Button
            variant="link"
            className="h-auto p-0 text-sm text-muted-foreground"
            onClick={() => {
              setNewPassword("");
              setConfirmPassword("");
              setValidationError(undefined);
              go("create-password-decision");
            }}
          >
            Go back
          </Button>
        </div>
      </>
    );
  }

  function renderStep() {
    switch (step) {
      case "email":
        return renderEmail();
      case "password":
        return renderPassword();
      case "auth-code":
        return renderAuthCode();
      case "create-password-decision":
        return renderCreatePasswordDecision();
      case "create-password":
        return renderCreatePassword();
    }
  }

  return (
    <div className="flex flex-col items-center justify-center p-6 md:p-12">
      {renderStep()}
    </div>
  );
}

export { FormAuthenticate };
export type { AuthenticateStep, UserKind };
